import { getTrackORPCClientInstance } from "@/lib/orpc/track-orpc.client"
import {
	generateCommonProperties,
	generateEventProperties,
	TRACKING_CONFIG,
} from "./tracking-config"
import { getClickElementInfo } from "./tracking-utils"

// 上报状态枚举
export enum TrackReportState {
	IMMEDIATE = "immediate", // 立即上报状态
	DELAYED = "delayed", // 延迟上报状态
}

// 追踪事件接口
interface TrackEvent {
	topic: string
	event: string
	properties: Record<string, any>
	timestamp: number
	session?: any
}

// 埋点服务类
export class TrackService {
	private static instance: TrackService
	private trackClient = getTrackORPCClientInstance()
	private sessionId: string | null = null

	// 批量上报相关
	private eventQueue: TrackEvent[] = []
	private batchSize: number = 5 // 批量大小 5-10个
	private batchTimeout: number = 2000 // 批量超时时间（毫秒）
	private reportTimer: NodeJS.Timeout | null = null
	private reportState: TrackReportState = TrackReportState.IMMEDIATE // 上报策略状态
	private isProcessing: boolean = false

	private constructor() {
		this.initSessionId()
		this.initBatchReport()
	}

	public static getInstance(): TrackService {
		if (!TrackService.instance) {
			TrackService.instance = new TrackService()
		}
		return TrackService.instance
	}

	private initSessionId(): void {
		if (typeof window !== "undefined") {
			// 从 sessionStorage 获取或生成新的 sessionId
			this.sessionId =
				sessionStorage.getItem("track_session_id") || this.generateSessionId()
			sessionStorage.setItem("track_session_id", this.sessionId)
		}
	}

	private generateSessionId(): string {
		return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
	}

	private initBatchReport(): void {
		// 页面卸载时强制上报剩余事件
		if (typeof window !== "undefined") {
			window.addEventListener("beforeunload", () => {
				this.flushQueue()
			})

			// 页面隐藏时也尝试上报
			document.addEventListener("visibilitychange", () => {
				if (document.hidden) {
					this.flushQueue()
				}
			})
		}
	}

	/**
	 * 添加事件到队列
	 */
	private addToQueue(
		topic: string,
		event: string,
		properties: Record<string, any> = {},
		session?: any,
	): void {
		const trackEvent: TrackEvent = {
			topic,
			event,
			properties,
			timestamp: Date.now(),
			session, // 存储 session 信息
		}

		this.eventQueue.push(trackEvent)

		// 根据队列大小和事件类型决定上报策略
		this.decideReportStrategy()
	}

	/**
	 * 决定上报策略
	 * IMMEDIATE: 立即上报（页面浏览事件、队列满时）
	 * DELAYED: 延迟上报（普通事件，延迟2秒或队列满时上报）
	 */
	private decideReportStrategy(): void {
		const queueSize = this.eventQueue.length

		// 如果队列达到批量大小，立即上报
		if (queueSize >= this.batchSize) {
			this.reportState = TrackReportState.IMMEDIATE
			this.flushQueue()
			return
		}

		// 检查是否有页面浏览事件，如果有则立即上报
		const hasPageViewEvents = this.eventQueue.some(
			(e) => e.event === TRACKING_CONFIG.events.PAGE_VIEW,
		)
		if (hasPageViewEvents) {
			this.reportState = TrackReportState.IMMEDIATE
			this.flushQueue()
			return
		}

		// 其他事件使用延迟上报策略
		this.reportState = TrackReportState.DELAYED

		// 清除现有定时器，重新设置延迟上报
		if (this.reportTimer) {
			clearTimeout(this.reportTimer)
		}

		this.reportTimer = setTimeout(() => {
			this.flushQueue()
		}, this.batchTimeout)
	}

	/**
	 * 上报队列中的事件
	 */
	private async flushQueue(): Promise<void> {
		if (this.isProcessing || this.eventQueue.length === 0) {
			return
		}

		this.isProcessing = true
		let eventsToProcess: TrackEvent[] = []

		try {
			// 清除定时器
			if (this.reportTimer) {
				clearTimeout(this.reportTimer)
				this.reportTimer = null
			}

			// 获取当前队列的副本，避免在处理过程中队列被修改
			eventsToProcess = [...this.eventQueue]

			// 立即清空队列，避免重复处理
			this.eventQueue = []

			// 按照批量大小分批处理
			const batchSize = this.batchSize
			const totalEvents = eventsToProcess.length

			// 计算需要处理的批次数量
			const batches = Math.ceil(totalEvents / batchSize)

			for (let i = 0; i < batches; i++) {
				// 获取当前批次的事件
				const startIndex = i * batchSize
				const endIndex = Math.min(startIndex + batchSize, totalEvents)
				const eventsToReport = eventsToProcess.slice(startIndex, endIndex)

				// 转换事件格式 - 严格按照 RPC 接口结构
				const trackDataList = eventsToReport.map((event) => {
					const eventProperties = generateEventProperties(event.session)

					// 只包含 RPC 接口定义中允许的字段
					const finalData: any = {
						// 必需字段
						topic: event.topic,
						appId: eventProperties.appId,
						level: "info" as const,
						event: event.event,
						timestamp: event.timestamp || Date.now(),

						// 可选字段（严格按照接口定义）
						session_id: eventProperties.session_id,
						country: eventProperties.country,
						country_code: eventProperties.country_code,
						is_login: eventProperties.is_login,
						is_first_day: eventProperties.is_first_day,
						anonymous_id: eventProperties.anonymous_id,
						browser: eventProperties.browser,
						browser_engine: eventProperties.browser_engine,
						browser_version: eventProperties.browser_version,
						language: eventProperties.language,
						url: eventProperties.url,
						url_host: eventProperties.url_host,
						url_path: eventProperties.url_path,
						url_path_json: eventProperties.url_path_json,
						url_query: eventProperties.url_query,
						url_query_json: eventProperties.url_query_json,
						referrer_url: eventProperties.referrer_url,
						referrer_url_host: eventProperties.referrer_url_host,
						referrer_url_path: eventProperties.referrer_url_path,
						referrer_url_path_json: eventProperties.referrer_url_path_json,
						referrer_url_query: eventProperties.referrer_url_query,
						referrer_url_query_json: eventProperties.referrer_url_query_json,
						source_entry_url: eventProperties.source_entry_url,
						source_url: eventProperties.source_url,
						source_url_host: eventProperties.source_url_host,
						source_url_query: eventProperties.source_url_query,
						source_url_query_json: eventProperties.source_url_query_json,
						title: eventProperties.title,
						feature_group: eventProperties.feature_group,
						login_id: eventProperties.login_id,
						user_need_auth: eventProperties.user_need_auth,
						user_id: eventProperties.user_id,
						user_type: eventProperties.user_type,
						distinct_id: eventProperties.distinct_id,
						device_id: eventProperties.device_id,
						area: eventProperties.area,
						areaCode: eventProperties.areaCode,
						region: eventProperties.region,
						city: eventProperties.city,
						exposure_source: eventProperties.exposure_source,
					}

					// 过滤掉不在接口定义中的自定义属性
					if (event.properties && typeof event.properties === "object") {
						const allowedFields = [
							"topic",
							"appId",
							"level",
							"event",
							"timestamp",
							"session_id",
							"country",
							"country_code",
							"is_login",
							"is_first_day",
							"anonymous_id",
							"browser",
							"browser_engine",
							"browser_version",
							"language",
							"url",
							"url_host",
							"url_path",
							"url_path_json",
							"url_query",
							"url_query_json",
							"referrer_url",
							"referrer_url_host",
							"referrer_url_path",
							"referrer_url_path_json",
							"referrer_url_query",
							"referrer_url_query_json",
							"source_entry_url",
							"source_url",
							"source_url_host",
							"source_url_query",
							"source_url_query_json",
							"title",
							"feature_group",
							"login_id",
							"user_need_auth",
							"user_id",
							"user_type",
							"distinct_id",
							"device_id",
							"area",
							"areaCode",
							"region",
							"city",
							"exposure_source",
						]

						Object.entries(event.properties).forEach(([key, value]) => {
							if (!allowedFields.includes(key) && key !== "properties") {
								finalData[key] = value
							}
						})
					}

					return finalData
				})

				// 批量上报
				await this.trackClient.track.track({
					properties: generateCommonProperties(),
					list: trackDataList,
				})
			}
		} catch (error) {
			console.error("TrackService: 批量上报失败:", error)
			// 上报失败时，将事件重新放回队列
			if (eventsToProcess && eventsToProcess.length > 0) {
				this.eventQueue.unshift(...eventsToProcess)
			}
		} finally {
			this.isProcessing = false
		}
	}

	/**
	 * 设置批量上报配置
	 */
	public setBatchConfig(batchSize: number, batchTimeout: number): void {
		this.batchSize = batchSize
		this.batchTimeout = batchTimeout
	}

	/**
	 * 获取当前队列状态
	 */
	public getQueueStatus(): {
		size: number
		state: TrackReportState
		isProcessing: boolean
	} {
		return {
			size: this.eventQueue.length,
			state: this.reportState,
			isProcessing: this.isProcessing,
		}
	}

	/**
	 * 调试方法：获取队列中的事件详情
	 */
	public getQueueEvents(): TrackEvent[] {
		return [...this.eventQueue]
	}

	/**
	 * 调试方法：手动触发上报
	 */
	public debugFlush(): void {
		this.flushQueue()
	}

	/**
	 * 强制上报队列中的所有事件
	 */
	public async forceFlush(): Promise<void> {
		await this.flushQueue()
	}

	/**
	 * 获取设备类型对应的topic
	 */
	private getTopic(): string {
		const userAgent = navigator.userAgent
		const isMobile =
			/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
				userAgent,
			)
		return isMobile ? "m_web_sdk" : "web_sdk"
	}

	/**
	 * 发送页面浏览事件
	 */
	async trackPageView(
		properties?: Record<string, any>,
		session?: any,
	): Promise<void> {
		const topic = this.getTopic()
		this.addToQueue(
			topic,
			TRACKING_CONFIG.events.PAGE_VIEW,
			properties,
			session,
		)
	}

	/**
	 * 发送点击事件
	 */
	async trackClick(
		element: HTMLElement,
		properties?: Record<string, any>,
		session?: any,
	): Promise<void> {
		const elementInfo = getClickElementInfo(element)
		const elementData = {
			id: elementInfo.element_id || "",
			md5: "", // 需要计算 MD5
			resource_id: elementInfo.element_info || "",
			resource_type: elementInfo.element_tag || "",
			position: "", // 需要计算位置
			type: elementInfo.element_tag || "",
		}

		const topic = this.getTopic()
		this.addToQueue(
			topic,
			TRACKING_CONFIG.events.CLICK,
			{
				...properties,
				elementInfo: elementData,
			},
			session,
		)
	}

	/**
	 * 发送自定义事件
	 */
	async trackEvent(
		eventName: string,
		properties?: Record<string, any>,
		session?: any,
	): Promise<void> {
		const topic = this.getTopic()
		this.addToQueue(topic, eventName, properties, session)
	}

	/**
	 * 发送页面离开事件
	 */
	async trackPageLeave(
		properties?: Record<string, any>,
		session?: any,
	): Promise<void> {
		const topic = this.getTopic()
		this.addToQueue(
			topic,
			TRACKING_CONFIG.events.PAGE_LEAVE,
			properties,
			session,
		)
	}
}

// 导出单例实例
export const trackService = TrackService.getInstance()
